import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Nomination, NominationStatus } from '../../types/nomination';
import { NominationServiceWrapper } from '../../services/nominationServiceWrapper';
import { approveNomination, rejectNomination, publishNomination } from '../../services/nominationWorkflow';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { notifyNominationApproved, notifyNominationRejected, notifyNominationPublished } from '../../services/inAppNotification';
import { emailNominationApproved, emailNominationRejected, emailNominationPublished } from '../../services/emailNotification';
import { getAllUserEmails } from '../../services/userLoader';
import { getStatusBadgeClass, formatNominationDate, cleanReason } from './NominationCard';
import PageHeader from '../common/PageHeader';
import './ReviewQueue.css';

const ReviewQueue: React.FC = () => {
  const renderStart = performance.now();
  console.log('[Perf][ReviewQueue] render start');
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refresh } = useNotifications();
  const [nominations, setNominations] = useState<Nomination[]>([]);
  const [approvedNominations, setApprovedNominations] = useState<Nomination[]>([]);
  /*
   * Approver by nomination id, resolved from the audit trail.
   *
   * The approvedBy COLUMN is the primary source, but it only exists once the AppSync schema
   * carrying it has been applied -- and it is empty on every nomination approved before then,
   * which is all of the ones already sitting in this queue. The audit trail is already
   * deployed and already records the approver's email on every status change, so it answers
   * the same question today and retroactively.
   */
  const [approverByNomination, setApproverByNomination] = useState<Record<string, string>>({});
  const [noteInputs, setNoteInputs] = useState<Record<string, string>>({});
  const [expandedNotes, setExpandedNotes] = useState<Record<string, boolean>>({});
  const [rejectTarget, setRejectTarget] = useState<Nomination | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [publishTarget, setPublishTarget] = useState<Nomination | null>(null);
  const [skipNotifications, setSkipNotifications] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [activeTab, setActiveTab] = useState<'review' | 'publish'>('review');

  const loadQueue = useCallback(async () => {
    const startedAt = performance.now();
    console.log('[Perf][ReviewQueue] API start NominationServiceWrapper.getReviewQueue');
    const queue = await NominationServiceWrapper.getReviewQueue();
    console.log('[Perf][ReviewQueue] API complete NominationServiceWrapper.getReviewQueue', {
      durationMs: Math.round(performance.now() - startedAt),
      count: queue.length,
    });
    setNominations(queue);
    const allStartedAt = performance.now();
    console.log('[Perf][ReviewQueue] API start NominationServiceWrapper.getAllNominationsRaw');
    const all = await NominationServiceWrapper.getAllNominationsRaw();
    const approvedFromDb = all.filter(n => n.status === NominationStatus.APPROVED);
    console.log('[Perf][ReviewQueue] API complete NominationServiceWrapper.getAllNominationsRaw', {
      durationMs: Math.round(performance.now() - allStartedAt),
      count: all.length,
      approvedCount: approvedFromDb.length,
      allStatuses: [...new Set(all.map(n => n.status))],
    });
    // Merge: keep any optimistically-approved nominations that DB hasn't caught up with yet
    setApprovedNominations(prev => {
      const dbIds = new Set(approvedFromDb.map(n => n.id));
      const optimisticOnly = prev.filter(n => !dbIds.has(n.id) && n.status === NominationStatus.APPROVED);
      const merged = [...approvedFromDb, ...optimisticOnly];
      if (optimisticOnly.length > 0) {
        console.log('[ReviewQueue][Debug] Preserving optimistic approvals not yet in DB:', optimisticOnly.map(n => n.id));
      }
      return merged;
    });
  }, []);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  /*
   * Who approved each nomination, for the rows that do not carry it themselves.
   *
   * Only for the Ready to Publish tab, only for rows still missing an approver, and only once
   * per row -- an already-resolved id is never re-fetched, so switching tabs costs nothing.
   */
  useEffect(() => {
    if (activeTab !== 'publish') return;
    const missing = approvedNominations.filter(n => !n.approvedBy && !approverByNomination[n.id]);
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      const resolved: Record<string, string> = {};
      await Promise.all(missing.map(async nom => {
        try {
          const entries = await NominationServiceWrapper.getAuditEntries(nom.id);
          const forThis = entries.filter(e => !e.nominationId || e.nominationId === nom.id);
          const newestFirst = [...forThis].sort((a, b) =>
            String(b.timestamp || '').localeCompare(String(a.timestamp || ''))
          );
          /*
           * An explicitly named APPROVED action first. Failing that, the newest entry that
           * changed the status and nothing else: on a nomination whose status IS Approved, the
           * person who last set the status is the person who approved it. Rejections and
           * publishes are excluded because their updates carry extra fields, so they are
           * distinguishable from an approval even in the older generic entries.
           */
          const approval =
            newestFirst.find(e => e.action === 'APPROVED') ||
            newestFirst.find(e => /Fields updated: status(, approved)/.test(e.details || '')) ||
            newestFirst.find(e => (e.details || '').trim() === 'Fields updated: status');
          const who = approval?.performedBy || approval?.performedByName;
          if (who) resolved[nom.id] = who;
        } catch (err) {
          // A trail we cannot read means we do not know who approved it, which the card
          // already renders as nothing rather than as a guess.
          console.warn('[ReviewQueue] could not resolve approver for', nom.id, err);
        }
      }));
      if (!cancelled && Object.keys(resolved).length > 0) {
        setApproverByNomination(prev => ({ ...prev, ...resolved }));
      }
    })();

    return () => { cancelled = true; };
    // approverByNomination is read to skip resolved rows, not to trigger; including it would
    // re-run the effect with every answer it stores.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, approvedNominations]);

  const handleApprove = async (nomination: Nomination) => {
    console.log('[ReviewQueue][Debug] Before Approve — Needs Review:', nominations.length, 'Ready To Publish:', approvedNominations.length);
    console.log('[ReviewQueue][Debug] Approving nomination:', nomination.id, 'Current status:', nomination.status);
    const result = approveNomination(nomination);
    console.log('[ReviewQueue][Debug] approveNomination result:', result.success, 'New status:', result.nomination?.status);
    if (result.success && result.nomination) {
      /*
       * The approver is recorded as a FIELD, not only as the audit actor.
       *
       * Both were already being passed here -- the email as adminId for the audit entry -- but
       * nothing persisted it onto the nomination, so Ready to Publish could show that a
       * nomination was approved and not by whom. Reading it back out of the audit trail is not
       * a substitute: that records which fields changed, not what they changed to, so an
       * approval and an unrelated status edit are indistinguishable there.
       *
       * Left undefined when the signed-in email is unknown rather than written as 'unknown',
       * so the queue can tell 'nobody recorded it' from 'somebody called unknown approved it'.
       */
      const approvedBy = user?.email || undefined;
      const approvedAt = new Date().toISOString();
      await NominationServiceWrapper.updateNomination(
        nomination.id,
        { status: result.nomination.status, approvedAt, approvedBy },
        user?.email || 'unknown',
        user?.name || 'Unknown'
      );
      console.log('[ReviewQueue][Debug] DynamoDB update complete. Status sent:', result.nomination.status);

      // Optimistically update local state — remove from review queue, add to approved
      setNominations(prev => {
        const updated = prev.filter(n => n.id !== nomination.id);
        console.log('[ReviewQueue][Debug] After state update — Needs Review:', updated.length);
        return updated;
      });
      setApprovedNominations(prev => {
        // Carries approvedBy so the attribution appears on the optimistic row immediately,
        // rather than popping in seconds later when the background reload lands.
        const updated = [...prev, { ...nomination, status: NominationStatus.APPROVED, approvedAt, approvedBy, updatedAt: new Date().toISOString() }];
        console.log('[ReviewQueue][Debug] After state update — Ready To Publish:', updated.length);
        return updated;
      });

      // Notify Nominator only (nominee gets notified only after publish)
      notifyNominationApproved(nomination.nominatorId, [], user?.name || 'Reviewer', nomination.nomineeName, nomination.awardType, nomination.id);
      emailNominationApproved(nomination.nominatorId, [], user?.name || 'Reviewer', nomination.nomineeName, nomination.awardType, nomination.id);

      showToast(`${nomination.nomineeName} approved`, 'success');
      refresh();

      // Background reload — give DynamoDB time to propagate the write
      setTimeout(() => loadQueue(), 3000);
      // Second sync attempt for strongly-consistent read
      setTimeout(() => loadQueue(), 8000);
    }
  };

  const handleRejectClick = (nomination: Nomination) => { setRejectTarget(nomination); setRejectionReason(''); };

  const handleRejectConfirm = async () => {
    if (!rejectTarget || !rejectionReason.trim()) return;
    const result = rejectNomination(rejectTarget, rejectionReason.trim());
    if (result.success && result.nomination) {
      await NominationServiceWrapper.updateNomination(rejectTarget.id, { status: result.nomination.status, rejectionReason: rejectionReason.trim() }, user?.email || 'unknown', user?.name || 'Unknown');

      // Optimistically remove from review queue
      setNominations(prev => prev.filter(n => n.id !== rejectTarget.id));

      notifyNominationRejected(rejectTarget.nominatorId, user?.name || 'Reviewer', rejectTarget.nomineeName, rejectTarget.id, rejectionReason.trim());
      emailNominationRejected(rejectTarget.nominatorId, user?.name || 'Reviewer', rejectTarget.nomineeName, rejectTarget.id, rejectionReason.trim());
      showToast(`${rejectTarget.nomineeName} rejected`, 'success');
      setRejectTarget(null);
      refresh();

      // Background reload
      setTimeout(() => loadQueue(), 3000);
    }
  };

  const handlePublishClick = (nomination: Nomination) => {
    setPublishTarget(nomination);
    setSkipNotifications(false);
  };

  const handlePublishConfirm = async () => {
    if (!publishTarget) return;
    const nomination = publishTarget;
    const result = publishNomination(nomination, user?.email || 'unknown');
    if (result.success && result.nomination) {
      await NominationServiceWrapper.updateNomination(nomination.id, { status: result.nomination.status, publishedAt: result.nomination.publishedAt, publishedBy: result.nomination.publishedBy }, user?.email || 'unknown', user?.name || 'Unknown');

      // Optimistically remove from Ready to Publish
      setApprovedNominations(prev => prev.filter(n => n.id !== nomination.id));

      if (!skipNotifications) {
        const allEmails = await getAllUserEmails();
        notifyNominationPublished(allEmails, nomination.nomineeName, nomination.awardType, user?.name || 'Admin', nomination.nomineeId);
        emailNominationPublished(allEmails, nomination.nomineeName, nomination.awardType, user?.name || 'Admin', nomination.nomineeId);
      }
      showToast(`${nomination.nomineeName} published to feed${skipNotifications ? ' (notifications skipped)' : ''}`, 'success');
      setPublishTarget(null);
      refresh();

      // Background reload to sync with DB
      setTimeout(() => loadQueue(), 3000);
    }
  };

  const handleAddNote = async (nominationId: string) => {
    const content = noteInputs[nominationId]?.trim();
    if (!content) return;
    const result = await NominationServiceWrapper.addReviewerNote({ nominationId, content }, user?.email || 'unknown', user?.name || 'Unknown');
    if (result.success) { setNoteInputs(prev => ({ ...prev, [nominationId]: '' })); showToast('Note added', 'success'); loadQueue(); }
  };

  const toggleNotes = (id: string) => setExpandedNotes(prev => ({ ...prev, [id]: !prev[id] }));

  const currentItems = activeTab === 'review' ? nominations : approvedNominations;

  useEffect(() => {
    console.log('[Perf][ReviewQueue] render complete', {
      durationMs: Math.round(performance.now() - renderStart),
      reviewCount: nominations.length,
      approvedCount: approvedNominations.length,
      currentItems: currentItems.length,
      activeTab,
    });
  });

  return (
    <div className="rq-page">
      <PageHeader
        icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>}
        title="Review Queue"
        subtitle={`${nominations.length + approvedNominations.length} nominations pending action`}
        gradient="linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%)"
        accentColor="#3b82f6"
      />

      <div className="rq-tabs">
        <button className={`rq-tab ${activeTab === 'review' ? 'active' : ''}`} onClick={() => setActiveTab('review')}>
          Needs Review <span className="rq-tab-count">{nominations.length}</span>
        </button>
        <button className={`rq-tab ${activeTab === 'publish' ? 'active' : ''}`} onClick={() => setActiveTab('publish')}>
          Ready to Publish <span className="rq-tab-count">{approvedNominations.length}</span>
        </button>
      </div>

      <div className="rq-items">
        {currentItems.length === 0 ? (
          <div className="rq-empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
            <h3>{activeTab === 'review' ? 'No Nominations to Review' : 'No Nominations Ready to Publish'}</h3>
            <p>Check back later for new submissions.</p>
          </div>
        ) : (
          currentItems.map(nom => (
            <div className="rq-card" key={nom.id}>
              <div className="rq-card-header">
                <div className="rq-card-left">
                  {nom.nomineePhotoUrl ? (
                    <img src={nom.nomineePhotoUrl} alt={nom.nomineeName} className="rq-photo" />
                  ) : (
                    <div className="rq-avatar">{nom.nomineeName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}</div>
                  )}
                  <div>
                    <h3 className="rq-name" onClick={() => navigate(`/nominations/${nom.id}`)}>{nom.nomineeName}</h3>
                    <span className="rq-award">{nom.awardType}</span>
                  </div>
                </div>
                <div className="rq-card-status">
                  <span className={`nomination-status-badge ${getStatusBadgeClass(nom.status)}`}>{nom.status}</span>
                  {/*
                    * Rendered only when actually known. Nominations approved before the field
                    * existed have no value, and printing 'Approved by Unknown' against a real
                    * decision would be worse than printing nothing.
                    */}
                  {nom.status === NominationStatus.APPROVED &&
                    (nom.approvedBy || approverByNomination[nom.id]) && (
                    <span
                      className="rq-approved-by"
                      title={nom.approvedAt ? `Approved ${formatNominationDate(nom.approvedAt)}` : undefined}
                    >
                      Approved by <strong>{nom.approvedBy || approverByNomination[nom.id]}</strong>
                    </span>
                  )}
                </div>
              </div>

              <p className="rq-reason">{cleanReason(nom.reason)}</p>

              <div className="rq-meta">
                <span>By <strong>{nom.nominatorName}</strong></span>
                <span>{formatNominationDate(nom.createdAt)}</span>
              </div>

              {/* Reviewer Notes */}
              {nom.reviewerNotes.length > 0 && (
                <div className="rq-notes">
                  <button className="rq-notes-toggle" onClick={() => toggleNotes(nom.id)}>
                    {expandedNotes[nom.id] ? '▾' : '▸'} {nom.reviewerNotes.length} note{nom.reviewerNotes.length !== 1 ? 's' : ''}
                  </button>
                  {expandedNotes[nom.id] && nom.reviewerNotes.map(note => (
                    <div key={note.id} className="rq-note">
                      <div className="rq-note-header"><span>{note.reviewerName}</span><span>{formatNominationDate(note.createdAt)}</span></div>
                      <p>{note.content}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Actions */}
              {activeTab === 'review' && (
                <>
                  <div className="rq-note-input-row">
                    <input type="text" className="rq-note-input" placeholder="Add a note..." value={noteInputs[nom.id] || ''} onChange={(e) => setNoteInputs(prev => ({ ...prev, [nom.id]: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') handleAddNote(nom.id); }} />
                    <button className="rq-action-btn rq-btn-note" onClick={() => handleAddNote(nom.id)} disabled={!noteInputs[nom.id]?.trim()}>Add Note</button>
                  </div>
                  <div className="rq-actions">
                    <button className="rq-action-btn rq-btn-approve" onClick={() => handleApprove(nom)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5" /></svg> Approve
                    </button>
                    <button className="rq-action-btn rq-btn-reject" onClick={() => handleRejectClick(nom)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg> Reject
                    </button>
                  </div>
                </>
              )}
              {activeTab === 'publish' && (
                <div className="rq-actions">
                  <button className="rq-action-btn rq-btn-publish" onClick={() => handlePublishClick(nom)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg> Publish to Feed
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Rejection Modal */}
      {rejectTarget && (
        <div className="rq-overlay" onClick={() => setRejectTarget(null)}>
          <div className="rq-modal" onClick={e => e.stopPropagation()}>
            <h3>Reject Nomination</h3>
            <p className="rq-modal-sub">Rejecting nomination for <strong>{rejectTarget.nomineeName}</strong></p>
            <textarea className="rq-modal-textarea" placeholder="Provide a reason for rejection (required)" value={rejectionReason} onChange={e => setRejectionReason(e.target.value)} />
            <div className="rq-modal-actions">
              <button className="rq-action-btn rq-btn-note" onClick={() => setRejectTarget(null)}>Cancel</button>
              <button className="rq-action-btn rq-btn-reject" onClick={handleRejectConfirm} disabled={!rejectionReason.trim()}>Confirm Reject</button>
            </div>
          </div>
        </div>
      )}

      {/* Publish Confirmation Modal */}
      {publishTarget && (
        <div className="rq-overlay" onClick={() => setPublishTarget(null)}>
          <div className="rq-modal" onClick={e => e.stopPropagation()}>
            <div className="rq-publish-modal-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2"><path d="M22 2L11 13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            </div>
            <h3>Publish Nomination</h3>
            <p className="rq-modal-sub">
              You are about to publish the recognition for <strong>{publishTarget.nomineeName}</strong> ({publishTarget.awardType}).
            </p>
            <div className="rq-publish-warning">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <span>This nomination will be published to the feed and notified to the entire CreyenteOne crew.</span>
            </div>
            <label className="rq-publish-checkbox">
              <input
                type="checkbox"
                checked={skipNotifications}
                onChange={e => setSkipNotifications(e.target.checked)}
              />
              <span>Skip email and notification feed (publish silently)</span>
            </label>
            <div className="rq-modal-actions">
              <button className="rq-action-btn rq-btn-note" onClick={() => setPublishTarget(null)}>Cancel</button>
              <button className="rq-action-btn rq-btn-publish" onClick={handlePublishConfirm}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg> Publish
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className={`rq-toast ${toast.type}`} role="alert">{toast.message}</div>}
    </div>
  );
};

export default ReviewQueue;
